import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Download, Filter, Loader2, RotateCcw, ChevronRight, ChevronDown, Heart, Box } from 'lucide-react';
import {
  VERSION_FILTER_OPTIONS,
  VERSION_LABELS,
  versionMatchesFilter,
  getCanonicalRank,
  getPlacementForLane,
  isInRankingView,
  type VersionFilterCode,
  type RankingView
} from '@primetime/shared';
import type { CanonicalAssetGroupDto, CrateDto, CreateUnlockRequest, CreateUnlockResponse, UserEntitlementDto } from '@primetime/shared/apiContracts';
import { api, ApiError, getAuthToken, resolveApiUrl } from '../services/apiClient';
import { PreviewPlayer } from '../components/player/PreviewPlayer';
import { PaywallNotice } from '../components/ui/PaywallNotice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { dispatchAccountStateSync, onAccountStateSync } from '../features/account/accountStateSync';
import { indexTrackVersionEntitlements, shouldRefreshCratesEntitlements } from '../features/account/entitlementState';
import { useAccountCredits } from '../features/account/useAccountCredits';
import { getCreditCost } from '../features/access/catalogAccess';
import { getCrateDownloadPresentation } from './crateDownloadActions';
import { mixProducts, type MixProduct } from '../data/mixProducts';
import { buildCatalogDisplayRows, getCatalogVersionDisplayLabel, getCatalogVersionPreviewUrl, getPreferredVersion, type CatalogDisplayRow, type CatalogTrackRow, type CatalogTrackVersion } from '../features/catalog/displayRows';
import { buildCanonicalViewCrates, canonicalAssetGroupToTrackRow, dedupeCanonicalAssetGroups, getCanonicalAssetGroupPreviewUrl, matchesCanonicalTrackQuery } from '../features/catalog/canonicalCatalog';
import type { CanonicalViewCrate } from '../features/catalog/canonicalCatalog';
import { PrimePicksCarousel } from '../features/catalog/PrimePicksCarousel';
import { buildPrimePicksCarouselSlides, type PrimePicksCarouselSlide, type PrimePicksTaxonomyLane } from '../features/catalog/primePicksCarouselData';
import { CATALOG_TAXONOMY, type CatalogTaxonomyNode } from '../features/catalog/taxonomy';
import { useCanonicalCatalog } from '../features/catalog/useCanonicalCatalog';

type ContentType = VersionFilterCode;
type TrackRow = CatalogTrackRow;
interface VersionRow extends CatalogDisplayRow { creditCost: number }
type CrateVariant = 'dirty' | 'clean' | 'djEdit';
type GenreFilterGroup = {
  taxonomyKey: string;
  label: string;
  laneKey: string | null;
  laneType: 'genre' | 'subgenre';
  laneGenre: string;
  laneSubgenre: string | null;
  parentLaneKey: string | null;
  crates: CanonicalViewCrate[];
  laneAssetCount: number;
  children?: GenreFilterGroup[];
  isApprovedCrateCategory: boolean;
};
type EmptyBucketSelection = { key: string; label: string; context: string; groupPath: string; laneKey?: string };
type BucketTemplate = { key: RankingView; label: string };
type PendingUnlock = {
  row: VersionRow;
  variant: CrateVariant;
  version: CatalogTrackVersion;
  creditCost: number;
  idempotencyKey: string;
  isRenewal: boolean;
};
type DownloadTarget = {
  entitlementId: string;
  rowKey: string;
  variant: CrateVariant;
  version: CatalogTrackVersion;
};
const TRACK_TABLE_GRID = 'grid-cols-[56px_minmax(0,1fr)] lg:grid-cols-[56px_minmax(0,2.9fr)_100px_minmax(0,1fr)_104px_40px_40px_40px_1px_40px_40px_40px_16px]';
const TRACK_TABLE_COLUMN_LAYOUT = `grid ${TRACK_TABLE_GRID} items-center gap-3 lg:gap-4`;
const TRACK_TABLE_SCROLL_RAIL = 'grid grid-cols-[minmax(0,1fr)_18px]';
const TRACK_TABLE_SURFACE = 'w-full';
const TRACK_TABLE_HEADER = 'border-b border-white/10 bg-white/[0.035] text-left text-[11px] font-bold uppercase tracking-[0.14em] text-white/50';
const BPM_MIN = 0;
const BPM_MAX = 200;
const SUB_CRATE_LABELS: Record<string, string> = {
  PRIME_PICKS: 'Prime Picks Top 25',
  ESSENTIALS: 'Essentials Top 50',
  TOP_100: 'PrimeTime Top 100',
  YEAR_HITS: 'Year Hits'
};
const SUB_CRATE_SORT_ORDER: Record<string, number> = {
  PRIME_PICKS: 10,
  ESSENTIALS: 20,
  TOP_100: 30,
  YEAR_HITS: 40
};
const GLOBAL_BUCKETS: BucketTemplate[] = [
  { key: 'PRIME_PICKS', label: 'Prime Picks Top 25' },
  { key: 'ESSENTIALS', label: 'Essentials Top 50' },
  { key: 'TOP_100', label: 'PrimeTime Top 100' },
  { key: 'YEAR_HITS', label: 'Year Hits' }
];
const CURATION_ENTRY_HEIGHT = 'min-h-[52px] lg:min-h-[62px]';
const CRATE_VARIANTS: Array<{ key: CrateVariant; label: 'D' | 'C' | 'DJ'; name: string }> = [
  { key: 'dirty', label: 'D', name: 'Dirty' },
  { key: 'clean', label: 'C', name: 'Clean' },
  { key: 'djEdit', label: 'DJ', name: 'DJ' }
];

function getCrateKey(value?: string | null) {
  return value?.trim().toUpperCase().replace(/[-\s]+/g, '_') || '';
}

function getRankedBucketKeyFromText(value?: string | null) {
  const key = getCrateKey(value);
  if (!key) return null;
  if (key.includes('PRIME_PICKS')) return 'PRIME_PICKS';
  if (key.includes('ESSENTIALS')) return 'ESSENTIALS';
  if (key.includes('PRIMETIME_TOP_100') || key.includes('TOP_100') || key.includes('TOP100')) return 'TOP_100';
  if (key.includes('YEAR_HITS') || key.includes('YEARHITS')) return 'YEAR_HITS';
  return null;
}

function formatCrateGroupLabel(value: string) {
  return value.trim();
}

function formatDuration(seconds?: number | null) {
  if (!seconds) return '--:--';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

function formatCatalogDate(value?: string | null) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatRank(rank?: number | null) {
  if (!rank || rank < 1) return '—';
  return String(rank);
}

function getDisplayedVersionDate(row: VersionRow) {
  return row.version.releaseDate || row.version.release_date || row.date;
}

function getGenreStyleLabel(row: TrackRow) {
  if (row.genre === 'Open Format' || row.genre === 'Lounge') {
    return '';
  }
  return row.subgenre || row.genre || '';
}

function getCrateGroupLabel(crate: CrateDto) {
  if (crate.laneType === 'subgenre' && crate.laneSubgenre) return crate.laneSubgenre;
  if (crate.laneGenre) return crate.laneGenre;
  if (crate.crateGroup) return formatCrateGroupLabel(crate.crateGroup);
  return crate.market === 'LAT' ? 'LATIN' : 'ENGLISH';
}

function getCrateDisplayLabel(crate: CrateDto) {
  const subCrateKey = getCrateKey(crate.subCrate);
  if (subCrateKey && SUB_CRATE_LABELS[subCrateKey]) return `${getCrateGroupLabel(crate)} ${SUB_CRATE_LABELS[subCrateKey]}`;
  if (crate.bucketLabel) return crate.bucketLabel;
  const displayLabel = crate.displayName || crate.title;
  const rankedBucketKey = getRankedBucketKeyFromText(`${displayLabel} ${crate.id}`);
  if (rankedBucketKey && SUB_CRATE_LABELS[rankedBucketKey]) {
    if (rankedBucketKey === 'PRIME_PICKS') return displayLabel.replace(/Prime Picks(?: Top 25)?/i, SUB_CRATE_LABELS.PRIME_PICKS);
    if (rankedBucketKey === 'ESSENTIALS') return displayLabel.replace(/Essentials(?: Top 50)?/i, SUB_CRATE_LABELS.ESSENTIALS);
    return displayLabel.replace(/(?:PrimeTime\s+)?Top\s*100/i, SUB_CRATE_LABELS.TOP_100);
  }
  return displayLabel;
}

function getCrateSectionLabel(crate: CrateDto) {
  if (crate.bucketKind === 'YEAR_HITS') return 'Year Hits';
  return 'Core Crates';
}

function getCrateBucketLabel(crate: CrateDto) {
  const subCrateKey = getCrateKey(crate.subCrate);
  if (subCrateKey && SUB_CRATE_LABELS[subCrateKey]) return SUB_CRATE_LABELS[subCrateKey];
  if (crate.bucketKind === 'YEAR_HITS') return crate.bucketYear ? `${crate.bucketYear} Hits` : 'Year Hits';
  const groupLabel = getCrateGroupLabel(crate);
  const label = crate.bucketLabel || crate.displayName || crate.title;
  return label.replace(new RegExp(`^${groupLabel}\\s+`, 'i'), '');
}

function getCrateSortOrder(crate: CrateDto) {
  if (crate.displayOrder !== null && crate.displayOrder !== undefined) return crate.displayOrder;
  const subCrateKey = getCrateKey(crate.subCrate);
  if (subCrateKey && SUB_CRATE_SORT_ORDER[subCrateKey] !== undefined) return SUB_CRATE_SORT_ORDER[subCrateKey];
  if (crate.bucketKind === 'YEAR_HITS') return SUB_CRATE_SORT_ORDER.YEAR_HITS;
  return 999;
}

function getCrateBucketKey(crate: CrateDto) {
  const subCrateKey = getCrateKey(crate.subCrate);
  if (subCrateKey) return subCrateKey;
  if (crate.bucketKind === 'YEAR_HITS') return 'YEAR_HITS';
  return getCrateKey(getCrateBucketLabel(crate));
}

function getRankedBucketKey(crate?: CrateDto | null) {
  if (!crate) return null;
  if (crate.bucketKind === 'YEAR_HITS') return 'YEAR_HITS';
  const metadataKey = getRankedBucketKeyFromText(crate.subCrate) || getRankedBucketKeyFromText(getCrateBucketLabel(crate));
  if (metadataKey) return metadataKey;
  return getRankedBucketKeyFromText([
    crate.bucketLabel,
    crate.displayName,
    crate.title,
    crate.id
  ].filter(Boolean).join(' '));
}

function getRankedBucketKeyFromSelection(crate?: CrateDto | null, emptyBucket?: EmptyBucketSelection | null) {
  return getRankedBucketKey(crate) || getRankedBucketKeyFromText([
    emptyBucket?.key,
    emptyBucket?.label,
    emptyBucket?.context
  ].filter(Boolean).join(' '));
}

function findCrateForBucket(crates: CanonicalViewCrate[], bucketKey: string, year?: number) {
  return crates.find(crate => {
    const crateBucketKey = getCrateBucketKey(crate);
    if (bucketKey !== 'YEAR_HITS') return crateBucketKey === bucketKey;
    if (year === undefined) return crateBucketKey === 'YEAR_HITS' && !crate.bucketYear;
    return crateBucketKey === 'YEAR_HITS' && crate.bucketYear === year;
  });
}

function isCanonicalTrackInView(track: CatalogTrackRow, view: RankingView, laneKey: string, releaseYear?: number | null) {
  const placement = getPlacementForLane(track, laneKey);
  return Boolean(placement && isInRankingView(placement, view, track.releaseYear) &&
    (releaseYear === null || releaseYear === undefined || track.releaseYear === releaseYear));
}

function normalizeTaxonomyValue(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function matchesTaxonomyValue(value: string | null | undefined, candidates: string[] | undefined) {
  if (!value || !candidates?.length) return false;
  const normalizedValue = normalizeTaxonomyValue(value);
  return candidates.some((candidate) => normalizeTaxonomyValue(candidate) === normalizedValue);
}

function buildTaxonomyPlaceholderCrates(
  taxonomyKey: string,
  laneType: 'genre' | 'subgenre',
  laneGenre: string,
  laneSubgenre: string | null
): CanonicalViewCrate[] {
  return GLOBAL_BUCKETS.map((bucket) => {
    const isYearHits = bucket.key === 'YEAR_HITS';
    return {
      id: `taxonomy-${encodeURIComponent(taxonomyKey)}-${bucket.key.toLowerCase()}`,
      title: `${laneSubgenre || laneGenre} ${bucket.label}`,
      market: 'EN',
      periodLabel: 'taxonomy-shell',
      isActive: true,
      crateGroup: laneGenre,
      subCrate: bucket.key,
      displayName: `${laneSubgenre || laneGenre} ${bucket.label}`,
      displayOrder: isYearHits ? 40 : bucket.key === 'TOP_100' ? 30 : bucket.key === 'ESSENTIALS' ? 20 : 10,
      laneKey: null,
      laneType,
      parentLaneKey: null,
      laneGenre,
      laneSubgenre,
      bucketKind: isYearHits ? 'YEAR_HITS' : 'PRIMARY',
      bucketLabel: isYearHits ? 'Year Hits' : bucket.label,
      bucketYear: null,
      targetCount: isYearHits ? null : bucket.key === 'TOP_100' ? 100 : bucket.key === 'ESSENTIALS' ? 50 : 25,
      maxCapacity: null,
      assetCount: 0,
      laneAssetCount: 0
    };
  });
}

function findCanonicalCrateForTaxonomyNode(
  node: CatalogTaxonomyNode,
  laneType: 'genre' | 'subgenre',
  parentGenreMatchers: string[],
  crates: CanonicalViewCrate[]
) {
  return crates.find((crate) => {
    if (!crate.laneKey || crate.laneType !== laneType) return false;
    if (laneType === 'genre') return matchesTaxonomyValue(crate.laneGenre, node.matchGenres);
    return matchesTaxonomyValue(crate.laneSubgenre || node.label, node.matchStyles || [node.label]) &&
      matchesTaxonomyValue(crate.laneGenre, parentGenreMatchers);
  });
}

function buildLanePath(group: GenreFilterGroup) {
  return group.taxonomyKey;
}

function buildTaxonomyFilterGroups(
  nodes: CatalogTaxonomyNode[],
  crates: CanonicalViewCrate[],
  parentPath = '',
  parentGenreMatchers: string[] = []
): GenreFilterGroup[] {
  return nodes.map((node, index) => {
    const taxonomyKey = parentPath ? `${parentPath}/${index}-${normalizeTaxonomyValue(node.label)}` : `${index}-${normalizeTaxonomyValue(node.label)}`;
    const laneType: 'genre' | 'subgenre' = parentPath ? 'subgenre' : 'genre';
    const canonicalCrate = findCanonicalCrateForTaxonomyNode(node, laneType, parentGenreMatchers, crates);
    const laneKey = canonicalCrate?.laneKey || null;
    const laneGenre = canonicalCrate?.laneGenre || node.matchGenres?.[0] || parentGenreMatchers[0] || node.label;
    const laneSubgenre = laneType === 'subgenre' ? canonicalCrate?.laneSubgenre || node.matchStyles?.[0] || node.label : null;
    const groupCrates = canonicalCrate
      ? crates.filter((crate) => crate.laneKey === laneKey).sort((a, b) => getCrateSortOrder(a) - getCrateSortOrder(b))
      : buildTaxonomyPlaceholderCrates(
        taxonomyKey,
        laneType,
        laneGenre,
        laneSubgenre
      );
    const nextGenreMatchers = node.matchGenres?.length ? node.matchGenres : parentGenreMatchers;

    return {
      taxonomyKey,
      label: node.label,
      laneKey,
      laneType,
      laneGenre,
      laneSubgenre,
      parentLaneKey: canonicalCrate?.parentLaneKey || null,
      crates: groupCrates,
      laneAssetCount: canonicalCrate?.laneAssetCount || 0,
      children: node.children?.length ? buildTaxonomyFilterGroups(node.children, crates, taxonomyKey, nextGenreMatchers) : undefined,
      isApprovedCrateCategory: true
    };
  });
}

function flattenPrimePicksTaxonomyLanes(groups: GenreFilterGroup[]): PrimePicksTaxonomyLane[] {
  return groups.flatMap((group) => [
    {
      taxonomyKey: group.taxonomyKey,
      label: group.label,
      laneKey: group.laneKey,
      laneType: group.laneType,
      laneGenre: group.laneGenre,
      laneSubgenre: group.laneSubgenre
    },
    ...(group.children ? flattenPrimePicksTaxonomyLanes(group.children) : [])
  ]);
}

function groupContainsLaneKey(group: GenreFilterGroup, laneKey: string | null): boolean {
  if (!laneKey) return false;
  return group.laneKey === laneKey || Boolean(group.children?.some((child) => groupContainsLaneKey(child, laneKey)));
}

function findGenreFilterGroupByLaneKey(groups: GenreFilterGroup[], laneKey: string): GenreFilterGroup | null {
  for (const group of groups) {
    if (group.laneKey === laneKey) return group;
    const childMatch = group.children ? findGenreFilterGroupByLaneKey(group.children, laneKey) : null;
    if (childMatch) return childMatch;
  }
  return null;
}

function mixToTrackRow(mix: MixProduct): TrackRow {
  return {
    isMix: true,
    assetGroupId: mix.id,
    trackId: mix.id,
    rankingPlacements: [],
    releaseYear: Number(mix.releaseDate.slice(0, 4)) || null,
    date: mix.releaseDate,
    title: mix.title,
    artist: mix.creator,
    genre: mix.genre,
    style: mix.subGenre,
    bpm: 0,
    key: mix.bpmLabel,
    versions: [{
      trackVersionId: mix.id,
      versionType: 'CLEAN',
      durationSeconds: mix.durationMinutes * 60
    }],
    createdAt: mix.releaseDate,
    downloadCount: 0
  };
}

export function Crates() {
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTrackId = searchParams.get('track') || '';
  const autoPreviewTrackRef = useRef('');
  const autoCrateSearchRef = useRef('');
  const playlistScrollRef = useRef<HTMLDivElement | null>(null);
  const playlistScrollRailRef = useRef<HTMLDivElement | null>(null);
  const playlistThumbDragRef = useRef<{ startY: number; startScrollTop: number } | null>(null);
  const entitlementRefreshRequestRef = useRef(0);
  const inFlightActionRef = useRef<string | null>(null);

  const [activeCrate, setActiveCrate] = useState('');
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [playerTitle, setPlayerTitle] = useState('Select a track to preview');
  const [playerSubtitle, setPlayerSubtitle] = useState('Click a track name to preview');
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activePreviewKey, setActivePreviewKey] = useState<string | null>(null);
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(() => new Set());
  const [entitlementsByVersionId, setEntitlementsByVersionId] = useState<Map<string, UserEntitlementDto>>(() => new Map());
  const [activeActionKey, setActiveActionKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pendingUnlock, setPendingUnlock] = useState<PendingUnlock | null>(null);
  const [downloadRetry, setDownloadRetry] = useState<DownloadTarget | null>(null);

  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'popular'>('newest');
  const [minBpm, setMinBpm] = useState<number>(BPM_MIN);
  const [maxBpm, setMaxBpm] = useState<number>(BPM_MAX);
  const [minBpmInput, setMinBpmInput] = useState<string>(String(BPM_MIN));
  const [maxBpmInput, setMaxBpmInput] = useState<string>(String(BPM_MAX));
  const [selectedContent, setSelectedContent] = useState<ContentType[]>([]);
  const [versionsExpanded, setVersionsExpanded] = useState(false);
  const isVersionsExpanded = versionsExpanded || selectedContent.length > 0;
  const [expandedTaxonomy, setExpandedTaxonomy] = useState<Set<string>>(() => new Set());
  const [collapsedTaxonomy, setCollapsedTaxonomy] = useState<Set<string>>(() => new Set());
  const [activeEmptyBucket, setActiveEmptyBucket] = useState<EmptyBucketSelection | null>(null);
  const [activeTaxonomyKey, setActiveTaxonomyKey] = useState<string | null>(null);
  const [activeLaneKey, setActiveLaneKey] = useState<string | null>(null);
  const userId = user?.id || null;
  const {
    creditState,
    creditStateReady,
    markInsufficientCredits,
    requiresSubscribe
  } = useAccountCredits(userId, authLoading);
  const { assetGroups, status: catalogStatus } = useCanonicalCatalog();
  const canonicalAssetGroups = useMemo(() => dedupeCanonicalAssetGroups(assetGroups), [assetGroups]);
  const paywalled = catalogStatus === 'paywalled';
  const catalogLoading = catalogStatus === 'loading';
  const catalogEmpty = catalogStatus === 'ready' && canonicalAssetGroups.length === 0;
  const crates = useMemo(() => buildCanonicalViewCrates(canonicalAssetGroups), [canonicalAssetGroups]);
  const tracks = useMemo(
    () => canonicalAssetGroups.map(canonicalAssetGroupToTrackRow).filter((track) => track.versions.length > 0),
    [canonicalAssetGroups]
  );
  const [playlistScrollbar, setPlaylistScrollbar] = useState({ canScroll: false, thumbTop: 0, thumbHeight: 72 });
  const showMixProducts = selectedContent.includes('VIDEO_MIX') && !activeCrate && !activeEmptyBucket;
  const minBpmPercent = ((minBpm - BPM_MIN) / (BPM_MAX - BPM_MIN)) * 100;
  const maxBpmPercent = ((maxBpm - BPM_MIN) / (BPM_MAX - BPM_MIN)) * 100;

  useEffect(() => {
    const q = searchParams.get('q');
    if (q !== null) setQuery(q);
  }, [searchParams]);

  const updateSearchParam = (newQuery: string) => {
    setQuery(newQuery);
    const newParams = new URLSearchParams(searchParams);
    if (newQuery) newParams.set('q', newQuery);
    else newParams.delete('q');
    setSearchParams(newParams, { replace: true });
  };

  const selectCrate = (crateId: string, taxonomyKey?: string) => {
    setActiveCrate(crateId);
    setActiveEmptyBucket(null);
    setActiveTaxonomyKey(taxonomyKey || null);
    setQuery('');
    setSortBy('newest');
    setMinBpm(BPM_MIN);
    setMaxBpm(BPM_MAX);
    setMinBpmInput(String(BPM_MIN));
    setMaxBpmInput(String(BPM_MAX));
    setActiveLaneKey(null);
    setSelectedContent([]);
    setVersionsExpanded(false);
    setPlayerTitle('Select a track to preview');
    setPlayerSubtitle('Click a track name to preview');
    setActiveVideoUrl(null);
    setActivePreviewKey(null);
    setActionError(null);
    autoPreviewTrackRef.current = '';
    autoCrateSearchRef.current = '';

    if (selectedTrackId || query) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('track');
      newParams.delete('q');
      setSearchParams(newParams, { replace: true });
    }
  };

  const toggleTaxonomyPath = (path: string, isCurrentlyExpanded: boolean) => {
    if (isCurrentlyExpanded) {
      setExpandedTaxonomy(prev => { const next = new Set(prev); next.delete(path); return next; });
      setCollapsedTaxonomy(prev => new Set(prev).add(path));
    } else {
      setExpandedTaxonomy(prev => new Set(prev).add(path));
      setCollapsedTaxonomy(prev => { const next = new Set(prev); next.delete(path); return next; });
    }
  };

  const selectGenre = (group: GenreFilterGroup, path: string) => {
    setActiveCrate('');
    setActiveEmptyBucket(null);
    setActiveTaxonomyKey(path);
    setActiveLaneKey(group.laneKey);
    setSortBy('newest');
    setPlayerTitle('Select a track to preview');
    setPlayerSubtitle('Click a track name to preview');
    setActiveVideoUrl(null);
    setActivePreviewKey(null);
    setActionError(null);
    autoPreviewTrackRef.current = '';
    autoCrateSearchRef.current = '';

    if (selectedTrackId) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('track');
      setSearchParams(newParams, { replace: true });
    }
  };

  const selectEmptyBucket = (bucket: EmptyBucketSelection) => {
    setActiveCrate('');
    setActiveEmptyBucket(bucket);
    setActiveTaxonomyKey(bucket.groupPath);
    setActiveLaneKey(bucket.laneKey || null);
    setQuery('');
    setSortBy('newest');
    setMinBpm(BPM_MIN);
    setMaxBpm(BPM_MAX);
    setMinBpmInput(String(BPM_MIN));
    setMaxBpmInput(String(BPM_MAX));
    setSelectedContent([]);
    setVersionsExpanded(false);
    setExpandedTaxonomy(prev => {
      const next = new Set(prev);
      const parts = bucket.groupPath.split('/');
      let current = '';
      for (const part of parts) {
        current = current ? `${current}/${part}` : part;
        next.add(current);
      }
      return next;
    });
    setCollapsedTaxonomy(prev => {
      const next = new Set(prev);
      const parts = bucket.groupPath.split('/');
      let current = '';
      for (const part of parts) {
        current = current ? `${current}/${part}` : part;
        next.delete(current);
      }
      return next;
    });
    setPlayerTitle('Select a track to preview');
    setPlayerSubtitle('Click a track name to preview');
    setActiveVideoUrl(null);
    setActivePreviewKey(null);
    setActionError(null);
    autoPreviewTrackRef.current = '';
    autoCrateSearchRef.current = '';

    if (selectedTrackId || query) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('track');
      newParams.delete('q');
      setSearchParams(newParams, { replace: true });
    }
  };

  function getVariantVersion(row: VersionRow, variant: CrateVariant) {
    if (variant === 'dirty') return row.dirtyVersion;
    if (variant === 'clean') return row.cleanVersion;
    return row.djEditVersion;
  }

  function isPublishedVersion(version?: CatalogTrackVersion | null) {
    return version?.availability?.trim().toLowerCase() === 'available';
  }

  function hasPlayablePreview(version?: CatalogTrackVersion | null) {
    return Boolean(isPublishedVersion(version) && getCatalogVersionPreviewUrl(version));
  }

  function getDefaultPreviewVersion(row: VersionRow) {
    return [row.dirtyVersion, row.cleanVersion, row.djEditVersion].find((version) => hasPlayablePreview(version)) || null;
  }

  function getPreviewVariant(row: VersionRow, version?: CatalogTrackVersion | null) {
    if (!version) return null;
    const match = CRATE_VARIANTS.find((variant) => getVariantVersion(row, variant.key)?.trackVersionId === version.trackVersionId);
    return match?.key || null;
  }

  function playPreview(row: VersionRow, variant?: CrateVariant) {
    const selectedVersion = variant ? getVariantVersion(row, variant) : getDefaultPreviewVersion(row);
    const previewVersion = selectedVersion || getPreferredVersion(row);
    const previewLabel = selectedVersion
      ? getCatalogVersionDisplayLabel(selectedVersion, row)
      : row.displayVersionLabel || getCatalogVersionDisplayLabel(previewVersion, row);
    setPlayerTitle(`${row.title} — ${row.artistDisplay || row.artist} (${previewLabel})`);

    if (row.isMix) {
      const mix = mixProducts.find(m => m.id === row.trackId);
      setPlayerSubtitle(mix?.previewUrl ? '' : 'Preview not available');
      setActiveVideoUrl(mix?.previewUrl || null);
      setActivePreviewKey(null);
      return;
    }

    const previewUrl = hasPlayablePreview(selectedVersion) ? getCatalogVersionPreviewUrl(selectedVersion) : null;
    setPlayerSubtitle(previewUrl ? '' : 'Preview not available');
    setActiveVideoUrl(previewUrl);
    setActivePreviewKey(previewUrl ? `${row.rowKey}:${getPreviewVariant(row, selectedVersion)}` : null);
  }

  function playPrimePicksPreview(asset: CanonicalAssetGroupDto) {
    const editorLabel = asset.editLabel || asset.editorName;
    setPlayerTitle(`${asset.title} — ${asset.artist}${editorLabel ? ` (${editorLabel})` : ''}`);
    setPlayerSubtitle('');
    setActiveVideoUrl(getCanonicalAssetGroupPreviewUrl(asset.assetGroupId));
  }
  function renderPreviewButton(row: VersionRow, variant: CrateVariant, compact = false) {
    const definition = CRATE_VARIANTS.find((item) => item.key === variant)!;
    const version = getVariantVersion(row, variant);
    const playable = hasPlayablePreview(version);
    const isSelected = activePreviewKey === `${row.rowKey}:${variant}`;
    const className = compact
      ? `flex h-8 min-w-[40px] items-center justify-center rounded-md border px-2 text-[9px] font-black transition-colors ${isSelected ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'} ${!playable ? 'cursor-not-allowed opacity-35 hover:text-white/45' : ''}`
      : `flex h-8 min-w-[40px] items-center justify-center rounded-lg border px-2 text-[10px] font-black transition-colors ${isSelected ? 'border-prime-red bg-prime-red text-white shadow-glow' : 'border-white/10 bg-white/5 text-white/45 hover:text-white'} ${!playable ? 'cursor-not-allowed opacity-35 hover:text-white/45' : ''}`;

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          if (playable) playPreview(row, variant);
        }}
        className={className}
        disabled={!playable}
        aria-label={playable ? `Preview ${definition.name} version for ${row.title}` : `${definition.name} version preview unavailable for ${row.title}`}
        title={playable ? `Preview ${definition.name} version` : `${definition.name} preview unavailable`}
      >
        {definition.label}
      </button>
    );
  }

  const refreshEntitlements = useCallback(async () => {
    const requestId = ++entitlementRefreshRequestRef.current;
    if (!userId) {
      setEntitlementsByVersionId(new Map());
      return;
    }

    try {
      const entitlements = await api<UserEntitlementDto[]>('/api/account/entitlements');
      if (requestId !== entitlementRefreshRequestRef.current) return;
      setEntitlementsByVersionId(indexTrackVersionEntitlements(entitlements));
    } catch {
      if (requestId === entitlementRefreshRequestRef.current) setEntitlementsByVersionId(new Map());
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setWishlistIds(new Set());
      return;
    }
    api<{ trackVersionId: string }[]>('/api/account/wishlist').then((wishlist) => setWishlistIds(new Set(wishlist.map((track) => track.trackVersionId)))).catch(() => setWishlistIds(new Set()));
    void refreshEntitlements();
  }, [refreshEntitlements, userId]);

  useEffect(() => {
    return onAccountStateSync((detail) => {
      if (!shouldRefreshCratesEntitlements(userId, detail)) return;
      void refreshEntitlements();
    });
  }, [refreshEntitlements, userId]);

  const activeCrateDetails = useMemo(() => crates.find((crate) => crate.id === activeCrate) || null, [activeCrate, crates]);
  const activeRankedBucketKey = useMemo(
    () => getRankedBucketKeyFromSelection(activeCrateDetails, activeEmptyBucket),
    [activeCrateDetails, activeEmptyBucket]
  );
  const isActiveRankedView = Boolean(activeRankedBucketKey);
  const activeReleaseYear = activeCrateDetails?.bucketKind === 'YEAR_HITS' ? activeCrateDetails.bucketYear : null;
  const selectedLaneKey = activeCrateDetails?.laneKey || activeLaneKey;

  const visibleCatalogTracks = useMemo(() => {
    if (activeTaxonomyKey && !selectedLaneKey) return [];
    if (!selectedLaneKey) return tracks;
    if (!activeRankedBucketKey) return tracks.filter((track) => Boolean(getPlacementForLane(track, selectedLaneKey)));
    return tracks.filter((track) => isCanonicalTrackInView(track, activeRankedBucketKey as RankingView, selectedLaneKey, activeReleaseYear));
  }, [activeRankedBucketKey, activeReleaseYear, selectedLaneKey, tracks]);
  
  useEffect(() => {
    if (!selectedTrackId || crates.length === 0 || autoCrateSearchRef.current === selectedTrackId) return;
    autoCrateSearchRef.current = selectedTrackId;
    const selectedTrack = tracks.find((track) => track.assetGroupId === selectedTrackId || track.trackId === selectedTrackId);
    if (!selectedTrack) return;
    const targetCrate = crates.find((crate) => {
      if (!crate.laneKey || !getPlacementForLane(selectedTrack, crate.laneKey)) return false;
      const view = getRankedBucketKey(crate);
      return view ? isCanonicalTrackInView(selectedTrack, view as RankingView, crate.laneKey, crate.bucketYear) : false;
    }) || crates.find((crate) => Boolean(crate.laneKey && getPlacementForLane(selectedTrack, crate.laneKey)));
    if (targetCrate) {
      setActiveCrate(targetCrate.id);
      setActiveEmptyBucket(null);
      setActiveLaneKey(null);
    }
  }, [selectedTrackId, crates, tracks]);
  
  const combinedData = useMemo(() => {
    if (!showMixProducts) return visibleCatalogTracks;
    const normalizedMixes = mixProducts.filter(mix => mix.isActive).map(mixToTrackRow);
    return [...visibleCatalogTracks, ...normalizedMixes];
  }, [showMixProducts, visibleCatalogTracks]);

  const genreFilterGroups = useMemo<GenreFilterGroup[]>(() => {
    return buildTaxonomyFilterGroups(CATALOG_TAXONOMY, crates);
  }, [crates]);
  const primePicksCategories = useMemo(
    () => buildPrimePicksCarouselSlides(canonicalAssetGroups, flattenPrimePicksTaxonomyLanes(genreFilterGroups)),
    [canonicalAssetGroups, genreFilterGroups]
  );

  const viewPrimePicks = (category: PrimePicksCarouselSlide) => {
    if (!category.laneId || category.entries.length === 0) return;
    const primePicksCrate = crates.find((crate) => crate.laneKey === category.laneId && getRankedBucketKey(crate) === 'PRIME_PICKS');
    if (!primePicksCrate) return;
    const taxonomyGroup = findGenreFilterGroupByLaneKey(genreFilterGroups, category.laneId);
    selectCrate(primePicksCrate.id, taxonomyGroup?.taxonomyKey);
    window.requestAnimationFrame(() => {
      document.getElementById('catalog-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const activeLaneCrate = selectedLaneKey ? crates.find((crate) => crate.laneKey === selectedLaneKey) : null;
  const activeLaneLabel = activeLaneCrate ? getCrateGroupLabel(activeLaneCrate) : null;
  const activeViewLabel = activeCrateDetails ? getCrateDisplayLabel(activeCrateDetails) : activeEmptyBucket ? activeEmptyBucket.label : activeLaneLabel ? `${activeLaneLabel} Catalog` : 'Newest Videos';
  const activeViewContext = activeCrateDetails ? `${getCrateGroupLabel(activeCrateDetails)} / ${getCrateSectionLabel(activeCrateDetails)}` : activeEmptyBucket ? activeEmptyBucket.context : activeLaneLabel ? `${activeLaneLabel} / Catalog` : 'PrimeTime / Newest';

  const sortedAndFiltered = useMemo(() => {
    let result = combinedData.filter(t => {
      const selectedVersions = selectedContent.filter(content => content !== 'VIDEO_MIX');
      const showMixes = selectedContent.includes('VIDEO_MIX');

      if (selectedContent.length > 0) {
        if (t.isMix) {
          if (!showMixes) return false;
        } else {
          if (showMixes && selectedVersions.length === 0) return false;
          if (selectedVersions.length > 0 && !t.versions.some(v => selectedVersions.some(version => versionMatchesFilter(v.versionType, version)))) return false;
        }
      }

      const matchesSearch = matchesCanonicalTrackQuery(t, query);
      const matchesBpm = t.isMix || typeof t.bpm !== 'number' || (t.bpm >= minBpm && t.bpm <= maxBpm);
      return matchesSearch && matchesBpm;
    });

    result.sort((a, b) => {
      if (isActiveRankedView && !a.isMix && !b.isMix) {
        const aRank = selectedLaneKey ? getCanonicalRank(getPlacementForLane(a, selectedLaneKey) || { rank: Number.MAX_SAFE_INTEGER }) : null;
        const bRank = selectedLaneKey ? getCanonicalRank(getPlacementForLane(b, selectedLaneKey) || { rank: Number.MAX_SAFE_INTEGER }) : null;
        return (aRank || Number.MAX_SAFE_INTEGER) - (bRank || Number.MAX_SAFE_INTEGER);
      }
      if (sortBy === 'newest') return new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime();
      if (sortBy === 'oldest') return new Date(a.createdAt || a.date).getTime() - new Date(b.createdAt || b.date).getTime();
      if (sortBy === 'popular' && !a.isMix && !b.isMix) return (b.downloadCount || 0) - (a.downloadCount || 0);
      return 0;
    });

    return result;
  }, [combinedData, query, minBpm, maxBpm, selectedContent, sortBy, isActiveRankedView, selectedLaneKey]);

  const versionRows = useMemo<VersionRow[]>(() => buildCatalogDisplayRows(sortedAndFiltered).map(row => ({ ...row, creditCost: row.isMix ? 5 : getCreditCost(row.version.versionType) })), [sortedAndFiltered]);

  useEffect(() => {
    const playlist = playlistScrollRef.current;
    const rail = playlistScrollRailRef.current;
    if (!playlist || !rail) return;

    const updatePlaylistScrollbar = () => {
      const availableRailHeight = Math.max(rail.clientHeight - 12, 44);
      const maxScrollTop = Math.max(playlist.scrollHeight - playlist.clientHeight, 0);
      const canScroll = maxScrollTop > 1;
      const thumbHeight = canScroll
        ? Math.max(44, Math.round((playlist.clientHeight / playlist.scrollHeight) * availableRailHeight))
        : Math.min(88, availableRailHeight);
      const maxThumbTop = Math.max(availableRailHeight - thumbHeight, 0);
      const thumbTop = canScroll ? Math.round((playlist.scrollTop / maxScrollTop) * maxThumbTop) : 0;
      const next = { canScroll, thumbTop, thumbHeight };

      setPlaylistScrollbar(current => (
        current.canScroll === next.canScroll &&
        current.thumbTop === next.thumbTop &&
        current.thumbHeight === next.thumbHeight
          ? current
          : next
      ));
    };

    updatePlaylistScrollbar();
    playlist.addEventListener('scroll', updatePlaylistScrollbar, { passive: true });
    window.addEventListener('resize', updatePlaylistScrollbar);

    const resizeObserver = new ResizeObserver(updatePlaylistScrollbar);
    resizeObserver.observe(playlist);
    resizeObserver.observe(rail);

    return () => {
      playlist.removeEventListener('scroll', updatePlaylistScrollbar);
      window.removeEventListener('resize', updatePlaylistScrollbar);
      resizeObserver.disconnect();
    };
  }, [versionRows.length]);

  function scrollPlaylistFromRail(clientY: number) {
    const playlist = playlistScrollRef.current;
    const rail = playlistScrollRailRef.current;
    if (!playlist || !rail || !playlistScrollbar.canScroll) return;

    const railRect = rail.getBoundingClientRect();
    const maxScrollTop = playlist.scrollHeight - playlist.clientHeight;
    const availableRailHeight = Math.max(rail.clientHeight - 12, 44);
    const maxThumbTop = Math.max(availableRailHeight - playlistScrollbar.thumbHeight, 1);
    const nextThumbTop = Math.max(0, Math.min(clientY - railRect.top - 6 - playlistScrollbar.thumbHeight / 2, maxThumbTop));
    playlist.scrollTop = (nextThumbTop / maxThumbTop) * maxScrollTop;
  }

  function handlePlaylistRailPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    scrollPlaylistFromRail(event.clientY);
  }

  function handlePlaylistThumbPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    const playlist = playlistScrollRef.current;
    if (!playlist || !playlistScrollbar.canScroll) return;

    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    playlistThumbDragRef.current = {
      startY: event.clientY,
      startScrollTop: playlist.scrollTop
    };
  }

  function handlePlaylistThumbPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = playlistThumbDragRef.current;
    const playlist = playlistScrollRef.current;
    const rail = playlistScrollRailRef.current;
    if (!drag || !playlist || !rail) return;

    const maxScrollTop = playlist.scrollHeight - playlist.clientHeight;
    const availableRailHeight = Math.max(rail.clientHeight - 12, 44);
    const maxThumbTop = Math.max(availableRailHeight - playlistScrollbar.thumbHeight, 1);
    const scrollDelta = ((event.clientY - drag.startY) / maxThumbTop) * maxScrollTop;
    playlist.scrollTop = drag.startScrollTop + scrollDelta;
  }

  function handlePlaylistThumbPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    playlistThumbDragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }

  useEffect(() => {
    if (!selectedTrackId || autoPreviewTrackRef.current === selectedTrackId) return;
    const selectedRow = versionRows.find(row => row.trackId === selectedTrackId);
    if (!selectedRow) return;
    autoPreviewTrackRef.current = selectedTrackId;
    playPreview(selectedRow);
    window.requestAnimationFrame(() => {
      document.getElementById(`crate-version-${selectedRow.rowKey}`)?.scrollIntoView({
        block: 'start',
        behavior: 'smooth'
      });
    });
  }, [selectedTrackId, versionRows]);

  const clearFilters = () => {
    setSortBy('newest');
    setMinBpm(BPM_MIN);
    setMaxBpm(BPM_MAX);
    setMinBpmInput(String(BPM_MIN));
    setMaxBpmInput(String(BPM_MAX));
    setSelectedContent([]);
    setVersionsExpanded(false);
    setExpandedTaxonomy(new Set());
    setCollapsedTaxonomy(new Set());
    setActiveCrate('');
    setActiveEmptyBucket(null);
    setActiveTaxonomyKey(null);
    setActiveLaneKey(null);
    setQuery('');
    setPlayerTitle('Select a track to preview');
    setPlayerSubtitle('Click a track name to preview');
    setActiveVideoUrl(null);
    setActivePreviewKey(null);
    setActionError(null);
    autoPreviewTrackRef.current = '';
    autoCrateSearchRef.current = '';
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const toggleContent = (content: ContentType) => setSelectedContent(prev => prev.includes(content) ? prev.filter(c => c !== content) : [...prev, content]);

  function renderBucketButton(group: GenreFilterGroup, groupPath: string, label: string, bucketKey: string, depth: number, crate?: CanonicalViewCrate, contextPrefix = group.label) {
    const emptyKey = `${groupPath}/${bucketKey.toLowerCase()}`;
    const isActive = crate?.laneKey ? activeCrate === crate.id : activeEmptyBucket?.key === emptyKey;
    const onClick = () => {
      if (crate?.laneKey) {
        selectCrate(crate.id, group.taxonomyKey);
        return;
      }
      selectEmptyBucket({
        key: emptyKey,
        label,
        context: `${contextPrefix} / ${label}`,
        groupPath,
        laneKey: group.laneKey || undefined
      });
    };

    return (
      <button
        key={crate?.id || emptyKey}
        type="button"
        onClick={onClick}
        className={`flex w-full items-center gap-1.5 px-1 py-2 lg:py-1 ${bucketKey === 'PRIME_PICKS' || bucketKey === 'ESSENTIALS' || bucketKey === 'TOP_100' ? CURATION_ENTRY_HEIGHT : 'min-h-[52px] lg:min-h-0'} text-left text-[13px] rounded-sm transition-colors ${isActive ? 'bg-prime-red/10 text-prime-red border border-prime-red/20' : 'text-white/60 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}
        aria-pressed={isActive}
        title={`${label} (${crate?.assetCount ?? 0})`}
      >
        <div className="w-5 h-5 lg:w-4 lg:h-4 shrink-0" />
        <Box className={`w-5 h-5 lg:w-[18px] lg:h-[18px] shrink-0 ${isActive ? 'text-prime-red opacity-100' : 'text-white opacity-40'}`} />
        <span className={`truncate ${isActive ? 'font-bold' : ''}`}>{label}</span>
        <span className="ml-auto shrink-0 pl-2 text-[10px] font-black tabular-nums text-white/35">{crate?.assetCount ?? 0}</span>
      </button>
    );
  }

  function renderGlobalBucketButtons(group: GenreFilterGroup, groupPath: string, depth: number) {
    if (!group.isApprovedCrateCategory) return null;
    return GLOBAL_BUCKETS.map(bucket => {
      const crate = findCrateForBucket(group.crates, bucket.key);
      if (bucket.key !== 'YEAR_HITS') {
        return renderBucketButton(group, groupPath, bucket.label, bucket.key, depth, crate);
      }

      const yearCrates = group.crates
        .filter((candidate) => getCrateBucketKey(candidate) === 'YEAR_HITS' && candidate.bucketYear !== null && candidate.bucketYear !== undefined)
        .sort((a, b) => (b.bucketYear || 0) - (a.bucketYear || 0));
      const emptyKey = `${groupPath}/${bucket.key.toLowerCase()}`;
      const containsActiveYear = activeCrate && group.crates.find(c => c.id === activeCrate)?.bucketKind === 'YEAR_HITS';
      const isExpanded = expandedTaxonomy.has(emptyKey) || (!collapsedTaxonomy.has(emptyKey) && Boolean(containsActiveYear));
      const isActive = crate?.laneKey ? activeCrate === crate.id || yearCrates.some((yearCrate) => yearCrate.id === activeCrate) : activeEmptyBucket?.key === emptyKey;

      const handleToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleTaxonomyPath(emptyKey, isExpanded);
      };

      const handleSelect = (e: React.MouseEvent) => {
        toggleTaxonomyPath(emptyKey, isExpanded);
        if (crate?.laneKey) {
          selectCrate(crate.id, group.taxonomyKey);
        } else {
          selectEmptyBucket({
            key: emptyKey,
            label: bucket.label,
            context: `${group.label} / ${bucket.label}`,
            groupPath,
            laneKey: group.laneKey || undefined
          });
        }
      };

      return (
        <div key={emptyKey}>
          <div className={`flex w-full items-center gap-1 px-1 py-2 lg:py-1 ${CURATION_ENTRY_HEIGHT} text-left text-[13px] rounded-sm transition-colors ${isActive ? 'bg-prime-red/10 text-prime-red border border-prime-red/20' : 'text-white/60 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}>
            <button 
              type="button" 
              onClick={handleToggle}
              className="flex shrink-0 items-center justify-center w-5 h-5 lg:w-4 lg:h-4 opacity-60 hover:opacity-100 hover:bg-white/10 rounded-sm"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4 lg:w-3.5 lg:h-3.5" /> : <ChevronRight className="w-4 h-4 lg:w-3.5 lg:h-3.5" />}
            </button>
            <button type="button" onClick={handleSelect} className="flex flex-1 items-center gap-1.5 min-w-0 text-left py-0.5" title={`${bucket.label} (${crate?.assetCount ?? 0})`}>
              <Box className={`w-5 h-5 lg:w-[18px] lg:h-[18px] shrink-0 ${isActive ? 'text-prime-red opacity-100' : 'text-white opacity-40'}`} />
              <span className={`truncate ${isActive ? 'font-bold' : ''}`}>{bucket.label}</span>
              <span className="ml-auto shrink-0 pl-2 text-[10px] font-black tabular-nums text-white/35">{crate?.assetCount ?? 0}</span>
            </button>
          </div>
          {isExpanded && (
            <div className="mt-0.5 mb-1 space-y-0.5 border-l border-white/10 ml-2 pl-3 animate-in fade-in slide-in-from-top-1 duration-200">
              {yearCrates.map((yearCrate) => {
                const year = yearCrate.bucketYear!;
                return renderBucketButton(group, emptyKey, `${year} Hits`, `${bucket.key}_${year}`, depth + 1, yearCrate, `${group.label} / ${bucket.label}`);
              })}
            </div>
          )}
        </div>
      );
    });
  }

  function renderGenreFilterGroup(group: GenreFilterGroup, parentPath = '', depth = 0) {
    const path = buildLanePath(group);
    const hasBuckets = group.isApprovedCrateCategory;
    const hasChildren = Boolean(group.children?.length);
    const hasExpandableContent = hasBuckets || hasChildren;
    const isSelected = group.taxonomyKey === activeTaxonomyKey;
    const containsActiveCrate = groupContainsLaneKey(group, activeCrateDetails?.laneKey || '');
    const containsActiveLane = groupContainsLaneKey(group, activeLaneKey || '');
    const containsActiveEmptyBucket = Boolean(activeEmptyBucket && (activeEmptyBucket.groupPath === path || activeEmptyBucket.groupPath.startsWith(`${path}/`)));

    // Auto-expand when this branch contains the active crate or active empty bucket,
    // but only if the user has not explicitly collapsed it (path absent from expandedTaxonomy).
    // Explicit user toggles always win.
    const autoExpand = hasExpandableContent && (containsActiveCrate || containsActiveLane || containsActiveEmptyBucket);
    const isExpanded = expandedTaxonomy.has(path) || (autoExpand && !collapsedTaxonomy.has(path));

    const handleToggleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (hasExpandableContent) {
        toggleTaxonomyPath(path, isExpanded);
      }
    };

    const handleSelectClick = (e: React.MouseEvent) => {
      if (hasExpandableContent) {
        toggleTaxonomyPath(path, isExpanded);
      }
      if (!containsActiveCrate && !containsActiveEmptyBucket) {
        selectGenre(group, path);
      }
    };

    return (
      <div key={path}>
        <div 
          className={`flex w-full items-center gap-1 px-1 py-2 lg:py-1 ${depth === 0 ? 'min-h-[52px] lg:min-h-[62px]' : 'min-h-[52px] lg:min-h-0'} text-left text-[13px] rounded-sm transition-colors ${isSelected || containsActiveCrate || containsActiveEmptyBucket ? 'bg-prime-red/10 text-prime-red border border-prime-red/20' : 'text-white/70 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}
        >
          {hasExpandableContent ? (
            <button 
              type="button" 
              onClick={handleToggleClick}
              className="flex shrink-0 items-center justify-center w-5 h-5 lg:w-4 lg:h-4 opacity-60 hover:opacity-100 hover:bg-white/10 rounded-sm"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4 lg:w-3.5 lg:h-3.5" /> : <ChevronRight className="w-4 h-4 lg:w-3.5 lg:h-3.5" />}
            </button>
          ) : (
            <div className="w-5 h-5 lg:w-4 lg:h-4 shrink-0" />
          )}
          
          <button type="button" onClick={handleSelectClick} className="flex flex-1 items-center gap-1.5 min-w-0 text-left py-0.5" title={group.label}>
            <Box className={`w-5 h-5 lg:w-[18px] lg:h-[18px] shrink-0 ${isSelected || containsActiveCrate || containsActiveEmptyBucket ? 'text-prime-red opacity-100' : 'text-white opacity-40'}`} />
            <span className={`truncate ${isSelected || containsActiveCrate || containsActiveEmptyBucket ? 'font-bold' : ''}`}>{group.label}</span>
            <span className="ml-auto shrink-0 pl-2 text-[10px] font-black tabular-nums text-white/35">{group.laneAssetCount}</span>
          </button>
        </div>
        {isExpanded && (
          <div className="mt-0.5 mb-1 space-y-0.5 border-l border-white/10 ml-2 pl-3 animate-in fade-in slide-in-from-top-1 duration-200">
            {renderGlobalBucketButtons(group, path, depth + 1)}
            {group.children?.map(child => renderGenreFilterGroup(child, path, depth + 1))}
          </div>
        )}
      </div>
    );
  }

  function queueVersionUnlock(row: VersionRow, variant: CrateVariant, isRenewal = false) {
    const version = getVariantVersion(row, variant);
    if (!version || !isPublishedVersion(version)) return;
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    const creditCost = getCreditCost(version.versionType);
    if (!creditStateReady) return;
    if (requiresSubscribe(version.trackVersionId, creditCost)) {
      navigate('/pricing');
      return;
    }

    if (inFlightActionRef.current || pendingUnlock) return;
    setActionError(null);
    setDownloadRetry(null);
    setPendingUnlock({
      row,
      variant,
      version,
      creditCost,
      idempotencyKey: `crates-${isRenewal ? 'renewal' : 'unlock'}-${version.trackVersionId}-${crypto.randomUUID()}`,
      isRenewal
    });
  }

  async function downloadAuthorizedVersion(entitlementId: string, version: CatalogTrackVersion) {
    const token = getAuthToken();
    const response = await fetch(resolveApiUrl(`/api/downloads/${encodeURIComponent(entitlementId)}`), {
      method: 'GET',
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const body = await response.json() as { error?: string };
        message = body.error || message;
      } catch {
        // Preserve the HTTP status when the secure download response is not JSON.
      }
      throw new ApiError(response.status, message);
    }

    const contentType = response.headers.get('content-type')?.toLowerCase() || '';
    if (contentType.includes('application/json')) {
      const result = await response.json() as { url?: string };
      if (!result.url) throw new ApiError(502, 'Unable to start secure download.');
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      return;
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = version.downloadFilename || `${version.trackVersionId}.mp4`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  function getActionErrorMessage(error: unknown, fallback: string) {
    if (!(error instanceof ApiError)) return fallback;
    if (error.status === 402) return 'Not enough PrimeTime Credits are available for this unlock.';
    if (error.status === 403) return 'Active Member Access is required to unlock this curated DJ/VJ set tool.';
    return error.message || fallback;
  }

  async function completeAuthorizedDownload(target: DownloadTarget) {
    await downloadAuthorizedVersion(target.entitlementId, target.version);
    dispatchAccountStateSync({ refreshLibrary: true });
  }

  async function startAuthorizedDownload(target: DownloadTarget) {
    if (inFlightActionRef.current) return;

    const actionKey = `${target.rowKey}:${target.variant}:download`;
    inFlightActionRef.current = actionKey;
    setActiveActionKey(actionKey);
    setActionError(null);
    try {
      await completeAuthorizedDownload(target);
      setDownloadRetry(null);
    } catch (error) {
      setDownloadRetry(target);
      setActionError(error instanceof ApiError ? error.message : 'Unable to start secure download.');
    } finally {
      if (inFlightActionRef.current === actionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  async function confirmPendingUnlock() {
    const target = pendingUnlock;
    if (!target || authLoading || !user || !creditStateReady || inFlightActionRef.current) return;

    const unlockActionKey = `${target.row.rowKey}:${target.variant}:unlock`;
    inFlightActionRef.current = unlockActionKey;
    setActiveActionKey(unlockActionKey);
    setActionError(null);

    try {
      const request: CreateUnlockRequest = {
        productType: 'TRACK_VERSION',
        assetGroupId: target.row.assetGroupId,
        trackVersionId: target.version.trackVersionId,
        idempotencyKey: target.idempotencyKey
      };
      const result = await api<CreateUnlockResponse>('/api/unlocks', {
        method: 'POST',
        body: JSON.stringify(request)
      });

      dispatchAccountStateSync({ updatedCredits: result.updatedCredits, refreshLibrary: true });
      setPendingUnlock(null);
      setDownloadRetry(null);
      await refreshEntitlements();

      const downloadTarget: DownloadTarget = {
        entitlementId: result.entitlement.entitlementId,
        rowKey: target.row.rowKey,
        variant: target.variant,
        version: target.version
      };
      const downloadActionKey = `${downloadTarget.rowKey}:${downloadTarget.variant}:download`;
      inFlightActionRef.current = downloadActionKey;
      setActiveActionKey(downloadActionKey);

      try {
        await completeAuthorizedDownload(downloadTarget);
      } catch (error) {
        setDownloadRetry(downloadTarget);
        setActionError(error instanceof ApiError ? error.message : 'Unlock succeeded, but the secure download could not start.');
      } finally {
        if (inFlightActionRef.current === downloadActionKey) {
          inFlightActionRef.current = null;
          setActiveActionKey(null);
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 402) {
        await markInsufficientCredits(target.version.trackVersionId, target.creditCost);
        await refreshAuth();
      }
      setActionError(getActionErrorMessage(error, 'Unable to unlock this version.'));
    } finally {
      if (inFlightActionRef.current === unlockActionKey) {
        inFlightActionRef.current = null;
        setActiveActionKey(null);
      }
    }
  }

  function handleVersionDownload(row: VersionRow, variant: CrateVariant) {
    const version = getVariantVersion(row, variant);
    if (!version || !isPublishedVersion(version) || inFlightActionRef.current) return;

    const entitlement = entitlementsByVersionId.get(version.trackVersionId);
    if (!entitlement) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate('/pricing');
        return;
      }
      queueVersionUnlock(row, variant);
      return;
    }
    if (entitlement.remainingDownloads <= 0) {
      if (authLoading) return;
      const creditCost = getCreditCost(version.versionType);
      if (!creditStateReady) return;
      if (requiresSubscribe(version.trackVersionId, creditCost)) {
        navigate('/pricing');
        return;
      }
      queueVersionUnlock(row, variant, true);
      return;
    }

    void startAuthorizedDownload({
      entitlementId: entitlement.entitlementId,
      rowKey: row.rowKey,
      variant,
      version
    });
  }

  function renderDownloadButton(row: VersionRow, variant: CrateVariant, compact = false) {
    const definition = CRATE_VARIANTS.find((item) => item.key === variant)!;
    const version = getVariantVersion(row, variant);
    const entitlement = version ? entitlementsByVersionId.get(version.trackVersionId) : undefined;
    const published = isPublishedVersion(version);
    const isBusy = activeActionKey === `${row.rowKey}:${variant}:download` || activeActionKey === `${row.rowKey}:${variant}:unlock`;
    const exhausted = Boolean(entitlement && entitlement.remainingDownloads <= 0);
    const disabled = !version || !published || Boolean(activeActionKey);
    const className = compact
      ? `flex h-8 min-w-[48px] items-center justify-center gap-1 rounded-md border px-2 text-[9px] font-black transition-colors ${!disabled ? 'border-white/10 bg-white/5 text-white/55 hover:border-prime-red/60 hover:bg-white/[0.14] hover:text-white' : 'cursor-not-allowed border-white/10 bg-white/5 text-white/25'} ${isBusy ? 'opacity-70' : ''}`
      : `flex h-8 min-w-[48px] items-center justify-center gap-1 rounded-lg border px-2 text-[10px] font-black transition-colors ${!disabled ? 'border-white/10 bg-white/5 text-white/55 hover:border-prime-red/60 hover:bg-white/[0.14] hover:text-white' : 'cursor-not-allowed border-white/10 bg-white/5 text-white/25'} ${isBusy ? 'opacity-70' : ''}`;
    const ariaLabel = !version || !published
      ? `${definition.name} version unavailable for download for ${row.title}`
      : exhausted
        ? `Renew ${definition.name} version allowance for ${row.title}`
        : entitlement
          ? `Download ${definition.name} version for ${row.title}`
          : `Unlock ${definition.name} version for download for ${row.title}`;

    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          void handleVersionDownload(row, variant);
        }}
        className={className}
        disabled={disabled}
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-busy={isBusy}
      >
        {isBusy ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : <Download size={12} aria-hidden="true" />}
        {definition.label}
      </button>
    );
  }

  function renderSubscribeButton(compact = false) {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          navigate('/pricing');
        }}
        className={compact
          ? 'btn-primary flex h-8 min-w-[82px] items-center justify-center rounded-md px-2 text-[9px] font-black uppercase tracking-wide'
          : 'btn-primary flex h-8 min-w-[96px] items-center justify-center rounded-lg px-2 text-[10px] font-black uppercase tracking-wide'}
        aria-label="Subscribe for PrimeTime Credits"
      >
        SUBSCRIBE
      </button>
    );
  }

  function renderDownloadActions(row: VersionRow, compact = false) {
    const lockedVersions = CRATE_VARIANTS.map(({ key }) => {
      const version = getVariantVersion(row, key);
      const entitlement = version ? entitlementsByVersionId.get(version.trackVersionId) : undefined;
      const published = isPublishedVersion(version);
      const creditCost = version ? getCreditCost(version.versionType) : 0;
      const exhausted = Boolean(entitlement && entitlement.remainingDownloads <= 0);
      const needsPaidAction = Boolean(version && published && (!entitlement || exhausted));
      const needsSubscribe = Boolean(needsPaidAction && requiresSubscribe(version!.trackVersionId, creditCost));
      const creditStatePending = Boolean(needsPaidAction && (authLoading || creditState.status === 'loading'));
      const creditStateError = Boolean(needsPaidAction && user && creditState.status === 'error');
      return { key, version, entitlement, published, needsSubscribe, creditStatePending, creditStateError };
    });
    const presentation = getCrateDownloadPresentation(lockedVersions);

    return (
      <>
        {presentation.actions.map((action, index) => action.type === 'download'
          ? <span key={action.key}>{renderDownloadButton(row, action.key as CrateVariant, compact)}</span>
          : <span key={`subscribe-${index}`}>{renderSubscribeButton(compact)}</span>)}
      </>
    );
  }

  async function toggleWishlist(row: VersionRow) {
    if (!user || row.isMix) return;
    const trackVersionId = row.version.trackVersionId;
    const isSaved = wishlistIds.has(trackVersionId);
    setWishlistIds(current => { const next = new Set(current); if (isSaved) next.delete(trackVersionId); else next.add(trackVersionId); return next; });
    try { await api(`/api/account/wishlist/${trackVersionId}`, { method: isSaved ? 'DELETE' : 'POST', ...(isSaved ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ assetGroupId: row.assetGroupId }) }) }); } catch { setWishlistIds(current => { const next = new Set(current); if (isSaved) next.add(trackVersionId); else next.delete(trackVersionId); return next; }); }
  }

  if (paywalled) return <PaywallNotice />;

  const hasActiveFilters = Boolean(
    query ||
    activeLaneKey ||
    activeTaxonomyKey ||
    selectedContent.length > 0 ||
    sortBy !== 'newest' ||
    minBpm > BPM_MIN ||
    maxBpm < BPM_MAX ||
    activeCrate ||
    activeEmptyBucket
  );
  return (
    <>
      <PreviewPlayer
        title={playerTitle}
        subtitle={playerSubtitle}
        videoUrl={activeVideoUrl}
        onPlaybackStateChange={setIsPreviewPlaying}
      />
      <section className='mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-14 pt-6'>
        {catalogLoading && (
          <div
            role="status"
            className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/55"
          >
            <span>Loading canonical catalog…</span>
          </div>
        )}
        <PrimePicksCarousel
          categories={primePicksCategories}
          activePreviewAssetGroupId={activeVideoUrl?.startsWith('/api/preview/asset-groups/') ? decodeURIComponent(activeVideoUrl.split('/').pop() || '') : null}
          isPreviewPlaying={isPreviewPlaying}
          onPreview={playPrimePicksPreview}
          onViewPrimePicks={viewPrimePicks}
        />
        <div className='mb-8 grid gap-8 lg:grid-cols-[280px_1fr]'>
          <div className="hidden lg:block" />
          <div className="flex items-center justify-between gap-6 flex-wrap">
            {/* Left: search + mobile filter toggle */}
            <div className="flex items-center gap-4">
              <div className="relative w-[280px] lg:w-[420px]">
                <input className='form-input w-full bg-black/20 border-white/10 text-sm' placeholder='Search catalog...' value={query} onChange={e => updateSearchParam(e.target.value)} />
              </div>
              <button type="button" onClick={() => setFiltersOpen(open => !open)} className={`focus-red flex h-11 items-center gap-2 rounded-xl border px-4 text-xs font-black uppercase tracking-wider transition-all lg:hidden ${filtersOpen ? 'border-prime-red/50 bg-prime-red/15 text-prime-red shadow-glow' : 'border-white/10 bg-black/20 text-white/60 hover:border-white/20 hover:text-white'}`}>
                <Filter size={15} /> Filter By
              </button>
            </div>
            {/* Right: BPM Range — far right of toolbar row */}
            <div className="hidden lg:flex items-center gap-4 rounded-xl border border-white/10 bg-black/20 px-5 h-11 min-w-[420px] ml-auto">
              <span className="text-[11px] font-black uppercase tracking-widest text-white/60 whitespace-nowrap">BPM Range</span>
              <div className="flex items-center gap-3 flex-1">
                <input
                  type="number"
                  min={BPM_MIN}
                  max={BPM_MAX}
                  value={minBpmInput}
                  aria-label="Minimum BPM value"
                  onChange={(e) => {
                    setMinBpmInput(e.target.value);
                  }}
                  onBlur={() => {
                    const parsed = parseInt(minBpmInput, 10);
                    const clamped = isNaN(parsed) ? BPM_MIN : Math.max(BPM_MIN, Math.min(parsed, maxBpm));
                    setMinBpm(clamped);
                    setMinBpmInput(String(clamped));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const parsed = parseInt(minBpmInput, 10);
                      const clamped = isNaN(parsed) ? BPM_MIN : Math.max(BPM_MIN, Math.min(parsed, maxBpm));
                      setMinBpm(clamped);
                      setMinBpmInput(String(clamped));
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className="w-12 rounded-md border border-white/10 bg-white/5 px-1 py-0.5 text-center text-xs font-bold text-white/70 tabular-nums focus:border-prime-red/50 focus:outline-none focus:ring-1 focus:ring-prime-red/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <div className="bpm-range-pair flex-1" aria-label={`BPM range ${minBpm} to ${maxBpm}`}>
                  <div className="bpm-range-track" />
                  <div
                    className="bpm-range-selection"
                    style={{ left: `${minBpmPercent}%`, right: `${100 - maxBpmPercent}%` }}
                  />
                  <input
                    type="range"
                    min={BPM_MIN}
                    max={BPM_MAX}
                    step="1"
                    value={minBpm}
                    aria-label="Minimum BPM"
                    onChange={(e) => {
                      const newMin = Number(e.target.value);
                      const committed = Math.min(newMin, maxBpm);
                      setMinBpm(committed);
                      setMinBpmInput(String(committed));
                    }}
                    className={`bpm-range-thumb ${minBpm > BPM_MAX - 8 ? 'z-30' : 'z-20'}`}
                  />
                  <input
                    type="range"
                    min={BPM_MIN}
                    max={BPM_MAX}
                    step="1"
                    value={maxBpm}
                    aria-label="Maximum BPM"
                    onChange={(e) => {
                      const newMax = Number(e.target.value);
                      const committed = Math.max(newMax, minBpm);
                      setMaxBpm(committed);
                      setMaxBpmInput(String(committed));
                    }}
                    className="bpm-range-thumb z-20"
                  />
                </div>
                <input
                  type="number"
                  min={BPM_MIN}
                  max={BPM_MAX}
                  value={maxBpmInput}
                  aria-label="Maximum BPM value"
                  onChange={(e) => {
                    setMaxBpmInput(e.target.value);
                  }}
                  onBlur={() => {
                    const parsed = parseInt(maxBpmInput, 10);
                    const clamped = isNaN(parsed) ? BPM_MAX : Math.min(BPM_MAX, Math.max(parsed, minBpm));
                    setMaxBpm(clamped);
                    setMaxBpmInput(String(clamped));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const parsed = parseInt(maxBpmInput, 10);
                      const clamped = isNaN(parsed) ? BPM_MAX : Math.min(BPM_MAX, Math.max(parsed, minBpm));
                      setMaxBpm(clamped);
                      setMaxBpmInput(String(clamped));
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                  className="w-12 rounded-md border border-white/10 bg-white/5 px-1 py-0.5 text-center text-xs font-bold text-white/70 tabular-nums focus:border-prime-red/50 focus:outline-none focus:ring-1 focus:ring-prime-red/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>
            </div>
          </div>
        </div>

        <div className='grid gap-8 lg:grid-cols-[280px_1fr]'>
          <aside className={`border border-white/10 bg-white/[0.02] h-fit rounded-sm p-5 lg:sticky lg:top-24 lg:block ${filtersOpen ? 'block' : 'hidden'}`}>
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-4"> 
                <h2 className="text-xs font-black uppercase tracking-[0.24em] text-white">Filter By</h2> 
                <button 
                  onClick={clearFilters} 
                  className={hasActiveFilters 
                    ? "flex items-center gap-1.5 rounded-md border border-prime-red/30 bg-prime-red/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-prime-red transition-colors hover:bg-prime-red/20 active:bg-prime-red/10" 
                    : "flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-white/40 transition-colors hover:border-white/20 hover:text-white/70"}
                > 
                  <RotateCcw size={12} /> Reset 
                </button> 
              </div>

              <div>
                <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-white">Sort By</h3>
                <div className="grid grid-cols-1 gap-1">
                  <button onClick={() => setSortBy('oldest')} className={`flex items-center justify-between px-2 py-1.5 ${CURATION_ENTRY_HEIGHT} text-[13px] rounded-sm transition-colors ${sortBy === 'oldest' ? 'bg-prime-red/10 text-prime-red font-bold border border-prime-red/20' : 'text-white/60 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}>Oldest {sortBy === 'oldest' && <Check size={14} />}</button>
                  <button onClick={() => setSortBy('popular')} className={`flex items-center justify-between px-2 py-1.5 ${CURATION_ENTRY_HEIGHT} text-[13px] rounded-sm transition-colors ${sortBy === 'popular' ? 'bg-prime-red/10 text-prime-red font-bold border border-prime-red/20' : 'text-white/60 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}>Most Downloaded {sortBy === 'popular' && <Check size={14} />}</button>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-white">Genres</h3>
                <div className="grid grid-cols-1 gap-0.5 pr-2">
                  {genreFilterGroups.map((group) => renderGenreFilterGroup(group))}
                </div>
              </div>

              <div>
                <button 
                  onClick={() => setVersionsExpanded(prev => !prev)}
                  className="flex w-full items-center justify-between gap-1 mb-2 px-1 py-2 lg:py-0 min-h-[52px] lg:min-h-0 text-left transition-colors hover:bg-white/[0.04] rounded-sm group"
                >
                  <h3 className="text-xs font-black uppercase tracking-widest text-white">
                    Versions {!isVersionsExpanded && <span className="text-white/40 tracking-wider ml-1 text-[10px] font-bold transition-colors group-hover:text-white/60">— Click for more</span>}
                  </h3>
                  <div className="flex shrink-0 items-center justify-center w-5 h-5 lg:w-4 lg:h-4 opacity-60 group-hover:opacity-100 transition-opacity">
                    {isVersionsExpanded ? <ChevronDown className="w-4 h-4 lg:w-3.5 lg:h-3.5" /> : <ChevronRight className="w-4 h-4 lg:w-3.5 lg:h-3.5" />}
                  </div>
                </button>
                {isVersionsExpanded && (
                  <div className="grid grid-cols-1 gap-1 animate-in fade-in duration-200">
                    {VERSION_FILTER_OPTIONS.map(option => ( <button key={option.code} onClick={() => toggleContent(option.code)} className={`flex items-center justify-between px-2 py-1.5 text-[13px] rounded-sm transition-colors ${selectedContent.includes(option.code) ? 'bg-prime-red/10 text-prime-red font-bold border border-prime-red/20' : 'text-white/60 border border-transparent hover:text-white hover:bg-white/[0.04]'}`}> {option.label} {selectedContent.includes(option.code) && <Check size={14} />} </button>))}
                  </div>
                )}
              </div>
            </div>
          </aside>
          <main id="catalog-table" className="min-w-0">
            <div className='border border-white/10 bg-white/[0.02] overflow-hidden rounded-sm min-h-[600px]'>
              <div className="relative border-b border-white/10 bg-white/[0.025] px-4 py-4 lg:px-6">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/35">
                      {activeViewContext}
                    </p>
                    <h2 className="mt-1 truncate text-lg font-black text-white">
                      {activeViewLabel}
                    </h2>
                    {activeViewLabel === 'Newest Videos' && (
                      <p className="mt-0.5 text-xs text-white/40">
                        This section is automatically sorted from newest to oldest.
                      </p>
                    )}
                  </div>
                  <p className="text-xs font-bold text-white/45">
            {versionRows.length} matching {versionRows.length === 1 ? 'item' : 'items'}
                  </p>
                </div>
                {actionError && !pendingUnlock && (
                  <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-prime-red/30 bg-prime-red/10 px-3 py-2 text-xs font-bold text-red-100">
                    <span>{actionError}</span>
                    {downloadRetry && (
                      <button
                        type="button"
                        onClick={() => void startAuthorizedDownload(downloadRetry)}
                        disabled={Boolean(activeActionKey)}
                        className="rounded-md border border-prime-red/40 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wider text-prime-red transition hover:bg-prime-red/10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Retry Download
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className='overflow-x-auto custom-scrollbar'>
                <div className={TRACK_TABLE_SCROLL_RAIL}>
                  <div className={`${TRACK_TABLE_SURFACE} ${TRACK_TABLE_COLUMN_LAYOUT} ${TRACK_TABLE_HEADER} px-4 py-4 lg:px-6`}>
                    <div className="col-start-1 border-r border-white/10 pr-3 lg:col-start-1 lg:pr-4">RANK</div>
                    <div className="col-start-2 lg:col-start-2">
                      <div>Track</div>
                      <div className="text-[10px] font-semibold tracking-[0.08em] text-white/40">Artist</div>
                    </div>
                    <div className="hidden lg:col-start-3 lg:block">BPM / Key</div>
                    <div className="hidden lg:col-start-4 lg:block">Genre</div>
                    <div className="hidden lg:col-start-5 lg:block">Date</div>
                    <div className="hidden text-center lg:col-start-6 lg:col-span-3 lg:block">Preview</div>
                    <div className="hidden h-9 w-px self-center justify-self-center bg-white/20 lg:col-start-9 lg:block" aria-hidden="true" />
                    <div className="hidden text-center lg:col-start-10 lg:col-span-3 lg:block">Download</div>
                    <div className="hidden lg:col-start-13 lg:block" aria-hidden="true" />
                  </div>
                  <div aria-hidden="true" />
                </div>
                <div className={TRACK_TABLE_SCROLL_RAIL}>
                  <div
                    ref={playlistScrollRef}
                    className="catalog-playlist-scrollbar w-full divide-y divide-white/5 overflow-y-auto lg:max-h-[600px]"
                  >
                    {catalogLoading ? (
                      <div role="status" aria-label="Loading catalog tracks" className="space-y-3 p-4 lg:p-6">
                        {[0, 1, 2, 3, 4].map((item) => (
                          <div key={item} className="grid grid-cols-[56px_minmax(0,1fr)_92px] items-center gap-3 rounded-lg border border-white/5 bg-white/[0.025] px-3 py-4 lg:grid-cols-[64px_minmax(0,1.8fr)_minmax(0,1.1fr)_100px]">
                            <div className="h-7 w-11 animate-pulse rounded bg-white/[0.08]" />
                            <div className="space-y-2">
                              <div className="h-4 w-2/3 animate-pulse rounded bg-white/[0.08]" />
                              <div className="h-3 w-1/3 animate-pulse rounded bg-white/[0.05]" />
                            </div>
                            <div className="hidden h-3 w-16 animate-pulse rounded bg-white/[0.05] lg:block" />
                            <div className="hidden h-3 w-12 animate-pulse rounded bg-white/[0.05] lg:block" />
                          </div>
                        ))}
                      </div>
                    ) : versionRows.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-32 text-center text-white/40">
                        <Filter size={48} className="mb-4 opacity-10" />
                        {activeCrateDetails && (
                          <div className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-white/30">
                            {getCrateGroupLabel(activeCrateDetails)} / {getCrateDisplayLabel(activeCrateDetails)}
                          </div>
                        )}
                        {activeEmptyBucket && (
                          <div className="mb-3 text-[10px] font-black uppercase tracking-[0.18em] text-white/30">
                            {activeEmptyBucket.context}
                          </div>
                        )}
                        <p className="text-lg font-bold">{catalogEmpty ? 'No tracks are available in the catalog yet.' : 'No catalog items are available here yet.'}</p>
                        <p className="mt-2 max-w-md text-sm text-white/45">
                          {catalogEmpty ? 'Canonical catalog lanes and tracks will appear here when they are published.' : activeEmptyBucket ? 'This approved bucket is ready for curation. No videos are assigned here yet.' : 'Curation is in progress. Adjust filters or check another crate while approved edits are prepared.'}
                        </p>
                        {!catalogEmpty && (
                          <button onClick={clearFilters} className="focus-red mt-4 rounded-lg px-3 py-2 text-sm font-black text-prime-red transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-prime-red/10 hover:shadow-glow active:translate-y-0 active:scale-[0.97]">
                            Reset filters
                          </button>
                        )}
                      </div>
                    ) : (
                      versionRows.map(row => {
                        return (
                          <div
                            id={`crate-version-${row.rowKey}`}
                            key={row.rowKey}
                            className={`${TRACK_TABLE_SURFACE} ${TRACK_TABLE_COLUMN_LAYOUT} px-4 py-2 transition-colors group lg:px-6 lg:py-3 ${row.trackId === selectedTrackId ? 'bg-prime-red/10 ring-1 ring-inset ring-prime-red/35' : 'hover:bg-white/[0.03]'}`}
                          >
                            <div className="col-start-1 flex items-center justify-start border-r border-white/10 pr-3 text-left lg:col-start-1 lg:pr-4">
                              <span className="text-[24px] font-black leading-none tabular-nums text-white">
                                {formatRank(selectedLaneKey ? getCanonicalRank(getPlacementForLane(row, selectedLaneKey) || { rank: 0 }) : null)}
                              </span>
                            </div>

                            <div className="col-start-2 min-w-0 text-left outline-none lg:col-start-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  playPreview(row);
                                }}
                                aria-label={`Preview ${row.title} by ${row.artistDisplay || row.artist}`}
                                className="focus-red block min-w-0 rounded-md text-left"
                              >
                                <div className="truncate text-[15px] font-semibold leading-5 text-white transition-colors group-hover:text-prime-red group-focus-within:text-prime-red">
                                  {row.title}
                                </div>
                                <div className="mt-0.5 flex min-w-0 items-center gap-2 leading-4">
                                  <span className="min-w-0 truncate text-xs font-medium text-white/45">{row.artistDisplay || row.artist}</span>
                                  <span className="h-1 w-1 shrink-0 rounded-full bg-white/20" />
                                  <span className="shrink-0 truncate text-[10px] font-black uppercase text-prime-red">
                                    {row.isMix ? 'Video Mix' : row.displayVersionLabel}
                                  </span>
                                </div>
                                <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px] font-semibold leading-4 text-white/40 lg:hidden">
                                  <span>{row.isMix ? row.key || '--' : `${row.bpm ?? '--'} BPM`}</span>
                                  <span>{row.isMix ? '--' : row.key || '--'}</span>
                                  <span>{getGenreStyleLabel(row)}</span>
                                </div>
                              </button>
                              <div className="mt-3 grid grid-cols-[1fr_auto] items-start gap-3 lg:hidden">
                                <div>
                                  <div className="mb-1 text-[9px] font-black uppercase tracking-[0.12em] text-white/35">Preview</div>
                                  <div className="flex items-center gap-1.5">
                                    {CRATE_VARIANTS.map(({ key }) => renderPreviewButton(row, key, true))}
                                  </div>
                                </div>
                                <div className="border-l border-white/10 pl-3">
                                  <div className="mb-1 text-[9px] font-black uppercase tracking-[0.12em] text-white/35">Download</div>
                                  <div className="flex items-center gap-1.5">
                                    {renderDownloadActions(row, true)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="hidden text-[13px] font-semibold leading-5 text-white/80 lg:col-start-3 lg:block">
                              {row.isMix ? row.key || '--' : `${row.bpm ?? '--'} / ${row.key || '--'}`}
                            </div>

                            <div className="hidden min-w-0 text-[13px] font-medium leading-5 text-white/60 lg:col-start-4 lg:block">
                              <div className="truncate">{getGenreStyleLabel(row)}</div>
                            </div>

                            <div className="hidden min-w-0 text-[13px] font-medium leading-5 text-white/60 lg:col-start-5 lg:block">
                              <div className="truncate">{formatCatalogDate(getDisplayedVersionDate(row))}</div>
                            </div>

                            <div className="hidden justify-center lg:col-start-6 lg:flex">
                              {renderPreviewButton(row, 'dirty')}
                            </div>

                            <div className="hidden justify-center lg:col-start-7 lg:flex">
                              {renderPreviewButton(row, 'clean')}
                            </div>

                            <div className="hidden justify-center lg:col-start-8 lg:flex">
                              {renderPreviewButton(row, 'djEdit')}
                            </div>

                            <div className="hidden h-9 w-px self-center justify-self-center bg-white/20 lg:col-start-9 lg:block" aria-hidden="true" />

                            <div className="hidden justify-center lg:col-start-10 lg:col-span-3 lg:flex">
                              {renderDownloadActions(row)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {versionRows.length > 0 && (
                    <div
                      ref={playlistScrollRailRef}
                      className="catalog-playlist-scroll-rail"
                      aria-hidden="true"
                      onPointerDown={handlePlaylistRailPointerDown}
                    >
                      <div
                        className="catalog-playlist-scroll-thumb"
                        style={{ height: `${playlistScrollbar.thumbHeight}px`, transform: `translateY(${playlistScrollbar.thumbTop}px)` }}
                        onPointerDown={handlePlaylistThumbPointerDown}
                        onPointerMove={handlePlaylistThumbPointerMove}
                        onPointerUp={handlePlaylistThumbPointerUp}
                        onPointerCancel={handlePlaylistThumbPointerUp}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
      </section>
      {pendingUnlock && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" role="presentation">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="crates-unlock-dialog-title"
            aria-describedby="crates-unlock-dialog-description"
            className="w-full max-w-[460px] rounded-2xl border border-white/15 bg-[#101010] p-5 shadow-2xl"
          >
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-prime-red">PRIMETIME CREDITS</p>
            <h2 id="crates-unlock-dialog-title" className="mt-2 text-xl font-black text-white">GET 5 DOWNLOADS</h2>
            <div className="mt-3 min-w-0">
              <p className="break-words text-[15px] font-black leading-5 text-white">{pendingUnlock.row.title}</p>
              <p className="mt-1 break-words text-[12px] leading-5 text-white/60">
                {pendingUnlock.row.artistDisplay || pendingUnlock.row.artist}
                <span className="mx-2 text-white/30">•</span>
                <span className="font-black uppercase text-prime-red">
                  {pendingUnlock.row.editLabel?.trim() || pendingUnlock.row.editorName?.trim() || VERSION_LABELS[pendingUnlock.version.versionType]}
                </span>
              </p>
            </div>
            <div className="mt-3 rounded-xl border border-white/15 bg-white/[0.04] p-3 text-xs">
              <div className="flex items-center justify-between gap-4">
                <span className="text-white/55">Version</span>
                <span className="font-black text-white">{VERSION_LABELS[pendingUnlock.version.versionType]}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Cost</span>
                <span className="font-black text-white">{pendingUnlock.creditCost} PrimeTime {pendingUnlock.creditCost === 1 ? 'Credit' : 'Credits'}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4 border-t border-white/10 pt-2">
                <span className="text-white/55">Downloads included</span>
                <span className="font-black text-white">5</span>
              </div>
            </div>
            <p id="crates-unlock-dialog-description" className="mt-3 text-[12px] leading-5 text-white/60">
              Your MP4 will download automatically after confirmation. Your remaining 4 downloads will be available in My Library.
            </p>
            {actionError && (
              <div role="alert" className="mt-4 rounded-lg border border-prime-red/30 bg-prime-red/10 px-3 py-2 text-xs font-bold text-red-100">
                {actionError}
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  if (activeActionKey) return;
                  setPendingUnlock(null);
                  setActionError(null);
                }}
                disabled={Boolean(activeActionKey)}
                className="w-full rounded-lg border border-white/15 px-4 py-2.5 text-xs font-black uppercase tracking-wider text-white/60 transition hover:border-white/30 hover:text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void confirmPendingUnlock()}
                disabled={activeActionKey === `${pendingUnlock.row.rowKey}:${pendingUnlock.variant}:unlock`}
                className="btn-primary w-full rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wider disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
              >
                {activeActionKey === `${pendingUnlock.row.rowKey}:${pendingUnlock.variant}:unlock`
                  ? pendingUnlock.isRenewal ? 'Renewing…' : 'Unlocking…'
                  : 'Confirm & Download'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
